/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Button, DatePicker, message, Input, Skeleton } from "antd";

const { RangePicker } = DatePicker;
import { ArrowLeftOutlined, InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { formatIndianNumber, mapReportCategoryToFigureType } from "../../../../utils";
import { DirectorDashboardService3 } from "../../../../services/api/directorDashboardService3";
import axios from "axios";
import "./style.scss";
import "../style.scss";



const ZoneListCentralOps = ({
    zones: propZones = ["EAST", "NORTH", "SOUTH1", "SOUTH2", "WEST1", "WEST2"],
    zoneData: propZoneData = {},
    businessType: propBusinessType = "",
    reportCategory: propReportCategory = "Shipment Counts",
    displayDate: propDisplayDate = "",
    onZoneClick: propOnZoneClick = null,
    selectedDate: propSelectedDate = null,
    onDateChange: propOnDateChange = null,
    onDateSubmit: propOnDateSubmit = null,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    // State for route-based usage
    const [selectedDate, setSelectedDate] = useState(moment());
    const [selectedDateRange, setSelectedDateRange] = useState([moment().startOf("month"), moment()]);
    const [businessType, setBusinessType] = useState("");
    const [reportCategory, setReportCategory] = useState("");
    const [reportType, setReportType] = useState("");
    const [dataType, setDataType] = useState("today");
    const [zoneData, setZoneData] = useState({});
    const [loading, setLoading] = useState(false);

    const hasLoadedRef = useRef(false);
    const isInitializingRef = useRef(true);
    const fetchInProgressRef = useRef(false);
    const lastFetchParamsRef = useRef(null);

    // Determine if component is used as route or as child component
    const isRouteComponent = propOnZoneClick === null;

    useEffect(() => {
        if (isRouteComponent) {
            // Get data from location state if used as route
            const category = location.state?.reportCategory || "Shipment Counts";
            const type = location.state?.reportType || "Piece Rate";
            const typeMode = location.state?.type || "today";
            const businessTypeValue = location.state?.businessType || "";

            isInitializingRef.current = true;

            setReportCategory(category);
            setReportType(type);
            setDataType(typeMode);
            setBusinessType(businessTypeValue);

            if (typeMode === "today") {
                if (location.state?.date) {
                    setSelectedDate(moment(location.state.date, "YYYY-MM-DD", true));
                }
            } else {
                // For range type
                if (location.state?.date && location.state?.endDate) {
                    const startDate = moment(location.state.date, "YYYY-MM-DD", true);
                    const endDate = moment(location.state.endDate, "YYYY-MM-DD", true);
                    if (startDate.isValid() && endDate.isValid()) {
                        setSelectedDateRange([startDate, endDate]);
                    }
                } else if (location.state?.date) {
                    const dateValue = moment(location.state.date, "YYYY-MM-DD", true);
                    if (dateValue.isValid()) {
                        setSelectedDateRange([dateValue, dateValue]);
                    }
                }
            }

            // Allow state updates to complete
            setTimeout(() => {
                isInitializingRef.current = false;
            }, 0);
        }
    }, [location.state, isRouteComponent]);

    // Fetch zone data from API
    const fetchZoneData = async (signal) => {
        if (!isRouteComponent) return; // Only fetch if used as route component

        // Don't call if still initializing or if required data is missing
        if (isInitializingRef.current || !reportCategory) return;

        // For Delivery Data, businessType might be empty, use default
        const finalBusinessType = businessType || (reportCategory === "Delivery Data" ? "Total DRS" : "Account");

        let fromDate, toDate;
        if (dataType === "today") {
            fromDate = selectedDate.format("YYYY-MM-DD");
            toDate = selectedDate.format("YYYY-MM-DD");
        } else {
            if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                fromDate = selectedDateRange[0].format("YYYY-MM-DD");
                toDate = selectedDateRange[1].format("YYYY-MM-DD");
            } else {
                fromDate = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                toDate = location.state?.endDate || location.state?.date || moment().format("YYYY-MM-DD");
            }
        }

        // Create a unique key for this fetch request
        const fetchKey = `${reportCategory}-${finalBusinessType}-${fromDate}-${toDate}`;

        // Prevent duplicate calls with same parameters or if already fetching
        // Note: With explicit abort controller, we might want to allow re-fetching, 
        // but keeping dedup logic for efficiency unless parameters change.
        if (fetchInProgressRef.current) {
            if (lastFetchParamsRef.current === fetchKey) {
                console.log("Fetch already in progress for same key, skipping");
                return;
            }
            // If new key, let it proceed (previous one should be cancelled by useEffect if it was from same effect)
        }

        if (lastFetchParamsRef.current === fetchKey) {
            console.log("Same parameters as last fetch, skipping duplicate call");
            return;
        }

        // Mark as in progress and store params
        fetchInProgressRef.current = true;
        lastFetchParamsRef.current = fetchKey;
        setLoading(true);

        try {
            console.log("Fetching zone data:", { reportCategory, finalBusinessType, fromDate, toDate });

            const figureType = mapReportCategoryToFigureType(reportCategory);
            // const figureSubtype = mapBusinessTypeToFigureSubtype(finalBusinessType, reportCategory);

            const response = await DirectorDashboardService3.getZoneHubCpData(
                figureType,
                finalBusinessType,
                fromDate,
                toDate,
                null,
                null,
                { signal }
            );

            if (response?.data?.zoneWiseSummary) {
                // Transform zoneWiseSummary array to object format
                const transformedData = {};
                response.data.zoneWiseSummary.forEach((item) => {
                    if (item.zoneName && item.zoneName !== "Total") {
                        transformedData[item.zoneName] = item.total || 0;
                    }
                });
                if (!signal?.aborted) setZoneData(transformedData);
            } else {
                if (!signal?.aborted) {
                    setZoneData({});
                    message.warning("No data available for the selected date range");
                }
            }
        } catch (error) {
            if (axios.isCancel(error)) return;
            console.error("Error loading zone data:", error);
            message.error(error?.message || "Failed to load zone data");
            if (!signal?.aborted) setZoneData({});
            // Reset lastFetchParamsRef on error so retry is possible
            lastFetchParamsRef.current = null;
        } finally {
            if (!signal?.aborted) {
                fetchInProgressRef.current = false;
                setLoading(false);
            }
        }
    };


    useEffect(() => {
        if (!isRouteComponent) return; // Only fetch if used as route component

        // eslint-disable-next-line no-unused-vars
        const controller = new AbortController();


        // Wait for initialization to complete
        const checkAndLoad = () => {
            if (isInitializingRef.current) {
                setTimeout(checkAndLoad, 50);
                return;
            }

            // Ensure we have required data
            if (!reportCategory) {
                return;
            }

            // Validate date is ready
            if (dataType === "today" && !selectedDate) {
                return;
            }
            if (dataType === "range" && (!selectedDateRange || !selectedDateRange[0] || !selectedDateRange[1])) {
                return;
            }

            // Use default businessType if empty
            const finalBusinessType = businessType || (reportCategory === "Delivery Data" ? "Total DRS" : "Account");

            const dateKey = dataType === "today"
                ? selectedDate.format("YYYY-MM-DD")
                : `${selectedDateRange[0]?.format("YYYY-MM-DD")}-${selectedDateRange[1]?.format("YYYY-MM-DD")}`;

            // Validate date key is complete
            if (!dateKey || dateKey.includes("null") || dateKey.includes("undefined")) {
                return;
            }

            const loadKey = `${reportCategory}-${finalBusinessType}-${dataType}-${dateKey}`;

            // Only fetch if loadKey changed and not already fetching
            if ((!hasLoadedRef.current || hasLoadedRef.current !== loadKey) && !fetchInProgressRef.current) {
                hasLoadedRef.current = loadKey;
                fetchZoneData(controller.signal);
            }
        };

        checkAndLoad();

        return () => controller.abort();
    }, [reportCategory, businessType, dataType, selectedDate, selectedDateRange, isRouteComponent]);


    const handleDateSubmit = () => {
        if (isRouteComponent) {
            hasLoadedRef.current = null; // Reset to allow reload
            lastFetchParamsRef.current = null; // Reset to allow manual fetch
            fetchInProgressRef.current = false; // Reset in case it's stuck
            fetchZoneData();
        } else if (propOnDateSubmit) {
            propOnDateSubmit();
        }
    };


    const handleZoneClick = (zone) => {
        if (zone === "Total") {
            return;
        }

        if (isRouteComponent) {
            // Map business type to reportType based on report category
            let reportTypeToPass = reportType;

            if (reportCategory === "Shipment Counts") {
                const reportTypeMap = {
                    "Cash Count": "Cash",
                    "Account Count": "Account",
                    "DP Counts": "DP",
                    "Piece Rate Count": "Piece Rate"
                };
                reportTypeToPass = reportTypeMap[businessType] || reportType;
            }

            // Navigate to hub-wise view
            const routeMap = {

                "Shipment Counts": "/central-ops/hub-wise-shipment-counts",
                "Delivery Data": "/central-ops/hub-wise-delivery-data",
            };
            navigate(routeMap[reportCategory] || "/central-ops/hub-wise-business-data", {
                state: {
                    zone: zone,
                    reportType: reportTypeToPass,
                    reportCategory: reportCategory,
                    type: dataType,
                    date: selectedDate.format("YYYY-MM-DD"),
                    businessType: businessType,
                },
            });
        } else if (propOnZoneClick) {
            propOnZoneClick(zone);
        }
    };

    // Use props if provided (child component), otherwise use state (route component)
    const finalZones = propZones;
    const finalZoneData = isRouteComponent ? zoneData : propZoneData;
    const finalBusinessType = isRouteComponent ? businessType : propBusinessType;
    const finalReportCategory = isRouteComponent ? reportCategory : propReportCategory;
    const finalSelectedDate = isRouteComponent ? selectedDate : (propSelectedDate || moment());

    const getDisplayDate = () => {
        if (!isRouteComponent) return propDisplayDate;

        if (dataType === "today") {
            return selectedDate.format("DD/MM/YYYY");
        } else {
            if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                return `${selectedDateRange[0].format("DD/MM/YYYY")} - ${selectedDateRange[1].format("DD/MM/YYYY")}`;
            }
            return `${moment().startOf("month").format("DD/MM/YYYY")} - ${moment().format("DD/MM/YYYY")}`;
        }
    };
    const finalDisplayDate = getDisplayDate();

    // Format value based on report category
    const formatValue = (value) => {
        const numValue = typeof value === "number" ? value : parseFloat(value) || 0;

        if (finalReportCategory === "Delivery Data" && finalBusinessType === "Delivery Percentage") {
            return `${numValue.toFixed(2)}%`;
        } else {
            return formatIndianNumber(numValue);
        }
    };

    // Calculate total
    const calculateTotal = () => {
        return finalZones.reduce((sum, zone) => {
            const value = finalZoneData[zone] || 0;
            return sum + (typeof value === "number" ? value : parseFloat(value) || 0);
        }, 0);
    };

    // Prepare table data
    const handleGoBack = () => {
        navigate("/central-ops-dashboard");
    };
    return (
        <div className="director-dashboard zone-wise-page">
            {/* Breadcrumb */}
            {/* Unified Header Bar */}
            <div className="zone-list-header">
                <div className="header-left">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleGoBack}
                        className="back-button"
                    />
                    <span className="page-title">
                        Zone Wise {finalReportCategory} - {finalDisplayDate} {finalBusinessType ? `- ${finalBusinessType}` : ""}
                    </span>
                </div>
                <div className="header-right">
                    <Input
                        placeholder="Search by Hub Name"
                        prefix={<SearchOutlined />}
                        className="search-input"
                    />
                    {isRouteComponent && dataType === "range" ? (
                        <RangePicker
                            format="DD/MM/YYYY"
                            value={selectedDateRange}
                            onChange={(dates) => {
                                if (dates && dates.length === 2) {
                                    setSelectedDateRange([dates[0], dates[1]]);
                                } else {
                                    setSelectedDateRange([moment().startOf("month"), moment()]);
                                }
                            }}
                            className="zone-wise-date-picker"
                        />
                    ) : (
                        <DatePicker
                            format="DD/MM/YYYY"
                            value={finalSelectedDate}
                            onChange={(date) => {
                                if (isRouteComponent) {
                                    setSelectedDate(date || moment());
                                } else if (propOnDateChange) {
                                    propOnDateChange(date || moment());
                                }
                            }}
                            className="zone-wise-date-picker"
                        />
                    )}
                    <Button type="primary" onClick={handleDateSubmit} className="zone-wise-submit-btn">
                        Submit
                    </Button>
                </div>
            </div>

            {/* Zone List Grid */}
            <div className="zone-list-grid">
                {/* Zone Cards */}
                {loading ? (
                    Array.from({ length: 6 }).map((_, index) => (
                        <div
                            className="zone-list-card"
                            key={`skeleton-${index}`}
                            style={{ cursor: "default" }}
                        >
                            <div className="card-header">
                                <Skeleton.Input active size="small" style={{ width: 80 }} />
                            </div>
                            <div className="card-value">
                                <Skeleton.Input active size="large" style={{ width: 100 }} />
                            </div>
                        </div>
                    ))
                ) : (
                    finalZones.map((zone) => (
                        <div
                            className="zone-list-card"
                            key={zone}
                            onClick={() => handleZoneClick(zone)}
                            style={{ cursor: "pointer" }}
                        >
                            <div className="card-header">
                                <span className="zone-name">{zone}</span>
                                <InfoCircleOutlined className="info-icon" />
                            </div>
                            <div className="card-value">
                                {formatValue(finalZoneData[zone] || 0)}
                            </div>
                        </div>
                    ))
                )}
            </div>


            {/* Total Footer */}
            <div className="zone-list-footer">
                <div className="footer-left">
                    <span className="footer-label">Total Zone Count : </span>
                    <span className="footer-value">{finalZones.length.toString().padStart(2, "0")}</span>
                </div>
                <div className="footer-right">
                    <span className="footer-label">
                        Total Count :
                    </span>
                    <span className="footer-value">{formatValue(calculateTotal())}</span>
                </div>

            </div>
        </div>
    );
};

export default ZoneListCentralOps;

